/**
 * WindGridLoader.ts
 * High-performance binary and JSON particle vector grid loader for Surface Winds and Jet Stream.
 * Supports compact 32-byte header + 16-bit integer packed buffers (wind_grid.bin) with seamless
 * fallback to legacy and clean JSON formats (wind_grid.json).
 */

import { authenticatedFetch } from '../config';

export interface WindGridData {
  west: number;
  south: number;
  east: number;
  north: number;
  grid_width: number;
  grid_height: number;
  u: Float32Array | number[];
  v: Float32Array | number[];
  gust: Float32Array | number[];
  header?: any;
  data?: any;
}

/**
 * Decodes a 32-byte header + 16-bit signed integer packed binary wind vector payload.
 * Header:
 *   [0..3]   magic 'WND1'
 *   [4..5]   version (uint16)
 *   [6..7]   flags (uint16)
 *   [8..11]  west (float32)
 *   [12..15] south (float32)
 *   [16..19] east (float32)
 *   [20..23] north (float32)
 *   [24..25] grid_width (uint16)
 *   [26..27] grid_height (uint16)
 *   [28..31] scale (float32, units in m/s)
 * Payload:
 *   u (int16 * N), v (int16 * N), gust (int16 * N) where N = width * height
 */
export function parseWindBinary(buffer: ArrayBuffer): WindGridData | null {
  if (buffer.byteLength < 32) return null;
  const view = new DataView(buffer);

  // Check magic 'WND1' (0x57, 0x4E, 0x44, 0x31)
  const m0 = view.getUint8(0);
  const m1 = view.getUint8(1);
  const m2 = view.getUint8(2);
  const m3 = view.getUint8(3);
  if (m0 !== 0x57 || m1 !== 0x4E || m2 !== 0x44 || m3 !== 0x31) {
    return null;
  }

  const west = view.getFloat32(8, true);
  const south = view.getFloat32(12, true);
  const east = view.getFloat32(16, true);
  const north = view.getFloat32(20, true);
  const grid_width = view.getUint16(24, true);
  const grid_height = view.getUint16(26, true);
  const scale = view.getFloat32(28, true) || 0.1;

  const nPoints = grid_width * grid_height;
  const expectedBytes = 32 + nPoints * 6;
  if (buffer.byteLength < expectedBytes) {
    console.warn(`[WindGridLoader] Binary buffer truncated: expected ${expectedBytes} bytes, got ${buffer.byteLength}`);
    return null;
  }

  const uRaw = new Int16Array(buffer, 32, nPoints);
  const vRaw = new Int16Array(buffer, 32 + nPoints * 2, nPoints);
  const gustRaw = new Int16Array(buffer, 32 + nPoints * 4, nPoints);

  const u = new Float32Array(nPoints);
  const v = new Float32Array(nPoints);
  const gust = new Float32Array(nPoints);

  for (let i = 0; i < nPoints; i++) {
    u[i] = uRaw[i] * scale;
    v[i] = vRaw[i] * scale;
    gust[i] = gustRaw[i] * scale;
  }

  return {
    west,
    south,
    east,
    north,
    grid_width,
    grid_height,
    u,
    v,
    gust
  };
}

/**
 * Normalizes legacy JSON structures (e.g. nested lists in data.u) into standard WindGridData.
 */
export function normalizeWindJson(grid: any): WindGridData | null {
  if (!grid) return null;

  if (grid.u && grid.v && Array.isArray(grid.u)) {
    return {
      west: grid.west ?? -180.0,
      south: grid.south ?? -90.0,
      east: grid.east ?? 180.0,
      north: grid.north ?? 90.0,
      grid_width: grid.grid_width ?? 360,
      grid_height: grid.grid_height ?? 181,
      u: grid.u,
      v: grid.v,
      gust: grid.gust || [],
      header: grid.header
    };
  }

  if (grid.data?.u && grid.header) {
    const uFlat = Array.isArray(grid.data.u[0]) ? grid.data.u.flat() : grid.data.u;
    const vFlat = Array.isArray(grid.data.v[0]) ? grid.data.v.flat() : grid.data.v;
    const gustFlat = grid.data.gust ? (Array.isArray(grid.data.gust[0]) ? grid.data.gust.flat() : grid.data.gust) : [];
    return {
      west: grid.header.lo1 ?? -180.0,
      south: grid.header.la2 ?? -90.0,
      east: grid.header.lo2 ?? 180.0,
      north: grid.header.la1 ?? 90.0,
      grid_width: grid.header.nx ?? 360,
      grid_height: grid.header.ny ?? 181,
      u: uFlat,
      v: vFlat,
      gust: gustFlat,
      header: grid.header
    };
  }

  return null;
}

/**
 * Fetches a wind grid asset. Prefers the high-speed binary TypedArray buffer (.bin)
 * and automatically falls back to .json if the binary asset is not available.
 */
export async function fetchWindGrid(url: string): Promise<WindGridData | null> {
  if (!url) return null;

  const binUrl = url.includes('.json') ? url.replace('.json', '.bin') : (url.endsWith('.bin') ? url : `${url}.bin`);
  const jsonUrl = url.includes('.bin') ? url.replace('.bin', '.json') : url;

  // 1. Try high-performance binary buffer (.bin)
  try {
    const binRes = await authenticatedFetch(binUrl);
    if (binRes.ok) {
      const buf = await binRes.arrayBuffer();
      const parsed = parseWindBinary(buf);
      if (parsed) {
        return parsed;
      }
    }
  } catch (err) {
    // Binary fetch failed; silently fall through to JSON
  }

  // 2. Fall back to JSON (.json)
  try {
    const jsonRes = await authenticatedFetch(jsonUrl);
    if (!jsonRes.ok) throw new Error(`HTTP ${jsonRes.status}`);
    const json = await jsonRes.json();
    return normalizeWindJson(json);
  } catch (err) {
    console.warn(`[WindGridLoader] Failed to load wind grid from ${url}:`, err);
    return null;
  }
}
